import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { graphql, compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import graphqlLib from '../../lib/graphql-lib';
import {
	Form,
	FormControl,
	Input,
	InputList,
	Dropdown,
	Button,
	Pane,
	Alert,
	Checkbox,
} from '../../components';

// Import the CSS
import '../../../scss/containers/customers/_add-edit-pane.scss';

const DEFAULT_STATE = {
	isEditing: false,
	isSaving: false,
	isLoadingManagers: false,
	nameValid: true,
	billingIdValid: true,
	dfpAdvertiserIdValid: true,
	allEmailsValid: true,
	managerOptions: [],
	errors: [],
};

const fields = ['billingId', 'dfpAdvertiserId', 'invoiceFields', 'managerUserId', 'emails'];

const invoiceFieldsMap = new Map([['billingId', 'PC'], ['dfpAdvertiserId', 'BOOST']]);

const validateFunctions = {
	name: name => new RegExp('^(?![^a-zA-Z0-9]*$).+').test(name),
	billingId: billingId => new RegExp('^(BLD|AGN)[0-9]{7}$', 'i').test(billingId),
	// optional fields
	dfpAdvertiserId: dfpAdvertiserId => new RegExp('^$|^[0-9]{7,10}$').test(dfpAdvertiserId),
	emails: email => new RegExp('^\\S+@\\S+\\.[\\S+]{2,}$').test(email),
};

class EditCustomerPane extends Pane {
	state = DEFAULT_STATE;

	componentWillReceiveProps(props) {
		const customer = props.customer || {};
		let selectedManagerOption = customer.managerUser
			? {
					label: (
						<span>
							{customer.managerUser.fullName}
							<br />
							<small>{customer.managerUser.email}</small>
						</span>
					),
					value: customer.managerUser.id,
			  }
			: null;

		this.setState({
			customer,
			selectedManagerOption,
		});
	}

	close() {
		// Reset the state, then close the panel
		this.setState(DEFAULT_STATE, () => super.close());
	}

	handleInputOnChange(fieldType, value) {
		const { invoiceFields } = this.state.customer;

		const isFieldValid = validateFunctions[fieldType](value || '');
		const isInvoiceField = invoiceFieldsMap.has(fieldType);

		const newCustomerState = {
			...this.state.customer,
			[fieldType]: value,
		};

		if (invoiceFields && isInvoiceField && (!value || !isFieldValid)) {
			let updatedInvoiceFields = invoiceFields.filter(
				field => field !== invoiceFieldsMap.get(fieldType),
			);

			if (!updatedInvoiceFields.length) {
				updatedInvoiceFields = null;
			}

			newCustomerState.invoiceFields = updatedInvoiceFields;
		}

		this.setState({
			isEditing: true,
			[`${fieldType}Valid`]: isFieldValid,
			customer: newCustomerState,
		});
	}

	async handleSubmitForm(e) {
		e.preventDefault();

		const { name, billingId } = this.state.customer;
		const trimmedName = name ? name.trim() : name;
		if (!trimmedName || !billingId) {
			return this.setState({
				nameValid: !!trimmedName,
				billingIdValid: !!billingId,
			});
		}

		this.setState({ isSaving: true, errors: [] });

		const input = _.pick(this.state.customer, fields);
		input.name = trimmedName;

		try {
			if (!this.state.customer.id) {
				await this.props.createCustomer(input);
			} else {
				input.id = this.state.customer.id;
				await this.props.updateCustomer(input);
			}

			this.setState(DEFAULT_STATE, () => this.props.onSave());
		} catch (err) {
			const errors = err.graphQLErrors
				? err.graphQLErrors.map(e => e.message)
				: ['Could not save the data.'];

			this.setState({ isSaving: false, errors });
		}
	}

	async handleAutoCompleteAccountManagers(value) {
		this.setState({ isFetchingManagerOptions: true });

		try {
			const {
				data: { users },
			} = await this.props.client.query({
				query: gql`
					query CustomerManagerQuery($query: String) {
						users(filter: { isEnabled: { eq: true }, query: $query }, limit: 5) {
							results {
								id
								fullName
								email
							}
						}
					}
				`,
				variables: {
					query: `%${value}%`,
				},
			});

			let managerOptions = [];

			if (users && users.results) {
				managerOptions = users.results;
			}

			this.setState({
				isFetchingManagerOptions: false,
				managerOptions,
			});
		} catch (err) {
			this.setState({
				isFetchingManagerOptions: false,
				managerOptions: [],
			});
		}
	}

	handleChangeSelectedManagerOption(option) {
		this.setState(prevState => ({
			selectedManagerOption: option,
			customer: {
				...prevState.customer,
				managerUserId: option.value,
			},
			isEditing: true,
		}));
	}

	handleClickInvoiceCheckbox(invoiceType, hasChecked) {
		const { invoiceFields } = this.state.customer;

		let updatedInvoiceFields;
		if (hasChecked) {
			updatedInvoiceFields = invoiceFields
				? invoiceFields.concat(invoiceType)
				: [invoiceType];
		} else {
			updatedInvoiceFields = invoiceFields.filter(field => field !== invoiceType);

			if (!updatedInvoiceFields.length) {
				updatedInvoiceFields = null;
			}
		}

		this.setState(prevState => ({
			customer: {
				...prevState.customer,
				invoiceFields: updatedInvoiceFields,
			},
			isEditing: true,
		}));
	}

	hasInvoiceField(type) {
		const { invoiceFields } = this.state.customer;

		return !!invoiceFields && invoiceFields.includes(type);
	}

	renderContent() {
		const {
			errors,
			customer,
			nameValid,
			billingIdValid,
			dfpAdvertiserIdValid,
			allEmailsValid,
			isFetchingManagerOptions,
			managerOptions,
			selectedManagerOption,
			isEditing,
			isSaving,
		} = this.state;
		const hasValidInputs =
			nameValid && billingIdValid && dfpAdvertiserIdValid && allEmailsValid;

		return (
			<Form onSubmit={e => this.handleSubmitForm(e)}>
				<If condition={errors.length}>
					{errors.map(error => (
						<Alert key={error} style="error">
							{error}
						</Alert>
					))}
				</If>

				<fieldset>
					<legend>Basic Info</legend>

					<FormControl hasError={!nameValid}>
						<label>
							Customer Name <span className="required">*</span>
						</label>
						<Input
							name="name"
							value={customer.name || ''}
							onChange={e => this.handleInputOnChange('name', e.target.value)}
						/>
						{!nameValid && (
							<div className="error-text">
								Please enter
								{!customer.name || !customer.name.trim() ? '' : ' valid'} customer
								name.
							</div>
						)}
					</FormControl>

					<FormControl hasError={!billingIdValid}>
						<label>
							Zillow Account ID <span className="required">*</span>
						</label>
						<Input
							name="billingId"
							value={customer.billingId || ''}
							onChange={e => this.handleInputOnChange('billingId', e.target.value)}
						/>
						<Checkbox
							name="customer-billing-id"
							className="invoice-checkbox"
							label="Use this to retrieve Invoices"
							checked={!!customer.billingId && this.hasInvoiceField('PC')}
							disabled={!billingIdValid || !customer.billingId}
							onChange={e => this.handleClickInvoiceCheckbox('PC', e.target.checked)}
						/>
						{!billingIdValid && (
							<div className="error-text">Please enter valid Zillow Account ID.</div>
						)}
					</FormControl>

					<FormControl hasError={!dfpAdvertiserIdValid}>
						<label>DFP Advertiser ID</label>
						<Input
							name="dfpAdvertiserId"
							value={customer.dfpAdvertiserId || ''}
							onChange={e =>
								this.handleInputOnChange('dfpAdvertiserId', e.target.value || null)
							}
						/>
						<Checkbox
							name="dfp-advertiser-id"
							className="invoice-checkbox"
							label="Use this to retrieve Invoices"
							checked={!!customer.dfpAdvertiserId && this.hasInvoiceField('BOOST')}
							disabled={!dfpAdvertiserIdValid || !customer.dfpAdvertiserId}
							onChange={e =>
								this.handleClickInvoiceCheckbox('BOOST', e.target.checked)
							}
						/>
						{!dfpAdvertiserIdValid && (
							<div className="error-text">Please enter valid DFP Advertiser ID.</div>
						)}
					</FormControl>
				</fieldset>

				<fieldset>
					<legend>Notification Recipients</legend>

					<FormControl>
						<label>Account Manager</label>
						<Dropdown
							autoComplete={value => this.handleAutoCompleteAccountManagers(value)}
							isFetchingOptions={isFetchingManagerOptions}
							name="managerUserId"
							placeholder="Search account managers..."
							emptyLabel="Select Option"
							options={managerOptions.map(user => ({
								label: (
									<span>
										{user.fullName}
										<br />
										<small>{user.email}</small>
									</span>
								),
								value: user.id,
							}))}
							selected={selectedManagerOption}
							onChange={option => this.handleChangeSelectedManagerOption(option)}
						/>
					</FormControl>

					<FormControl>
						<label>Additional Email</label>
						<InputList
							name="emails"
							values={_.map(customer.emails, 'email')}
							inputProps={{ type: 'email' }}
							onChange={emails => {
								const allEmailsValid = _.every(emails, validateFunctions.emails);
								// Convert the list of emails into a list of objects with an `email` parameter
								emails = emails.map(email => ({ email }));

								this.setState(prevState => ({
									isEditing: true,
									customer: {
										...prevState.customer,
										emails,
									},
									allEmailsValid,
								}));
							}}
							validateFunction={validateFunctions.emails}
							errorText="Please enter valid email address."
						/>
					</FormControl>
				</fieldset>

				<Button
					type="submit"
					size="small"
					className="pull-right"
					disabled={!isEditing || !hasValidInputs || isSaving}>
					{isSaving ? 'Saving...' : 'Save'}
				</Button>
			</Form>
		);
	}
}

const createCustomerMutation = gql`
	mutation CreateCustomerMutation($input: CreateCustomerInput!) {
		createCustomer(input: $input) {
			id
		}
	}
`;

const updateCustomerMutation = gql`
	mutation UpdateCustomerMutation($input: UpdateCustomerInput!) {
		updateCustomer(input: $input) {
			id
		}
	}
`;

export default compose(
	connect(state => ({
		user: state.auth.user,
	})),
	graphql(createCustomerMutation, {
		props: ({ mutate }) => ({
			createCustomer: input =>
				mutate({
					variables: {
						input: graphqlLib.removeTypename(input),
					},
				}),
		}),
	}),
	graphql(updateCustomerMutation, {
		props: ({ mutate }) => ({
			updateCustomer: input =>
				mutate({
					variables: {
						input: graphqlLib.removeTypename(input),
					},
				}),
		}),
	}),
	withApollo,
)(EditCustomerPane);



// WEBPACK FOOTER //
// ./src/assets/js/containers/customers/AddEditPane.js